import Dropin from 'braintree-web-drop-in';
import { ReactElement, useEffect, useRef } from 'react';
import { Component as WarningIcon } from 'icons/icon-color--warning.svg';
import { usePayment } from '../../../contexts/PaymentContext';
import styles from './PaymentForm.module.scss';

interface Props {
  dropinInstance,
  setDropinInstance: (dropinInstance) => void;
  paymentErrorMessage: string | null;
  handleSelectPaymentMethod: (selectedPaymentMethod) => void;
}

export function PaymentForm({
  dropinInstance,
  setDropinInstance,
  paymentErrorMessage,
  handleSelectPaymentMethod,
}: Props): ReactElement {
  const dropinContainerRef = useRef(null);
  const { token } = usePayment();
  const authorizationToken = token?.token;

  const braintreeOverride = {
    overrides: {
      fields: {
        number: {
          supportedCardBrands: {
            jcb: false,
            'union-pay': false,
          },
        },
      },
    },
  };

  useEffect(() => {
    if (dropinContainerRef.current === null
      || dropinInstance
      || authorizationToken === undefined) {
      return;
    }

    // Initialize Braintree Drop-in
    Dropin.create({
      authorization: authorizationToken,
      container: dropinContainerRef.current,
      card: braintreeOverride,
      googlePay: {
        merchantId: BRAINTREE_GOOGLE_PAY_MERCHANT_ID,
        googlePayVersion: 2,
        transactionInfo: {
          currencyCode: 'USD',
          countryCode: 'US',
          totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
        },
        allowedPaymentMethods: [{
          type: 'CARD',
          parameters: {
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
            billingAddressRequired: true,
            billingAddressParameters: {
              format: 'FULL',
            },
          },
        }],
      },
      paypal: {
        flow: 'vault',
        enableShippingAddress: true,
      },
      applePay: {
        displayName: 'Purple Carrot',
        paymentRequest: {
          countryCode: 'US',
          currencyCode: 'USD',
          merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
          supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
          total: {
            label: '$0 to subscribe with Apple Pay',
            type: 'pending',
            amount: '0',
          },
          requiredBillingContactFields: ['postalAddress'],
        },
      },
    }, (err, instance) => {
      if (err) return;

      instance.on('paymentMethodRequestable', async () => {
        const paymentDetails = await instance.requestPaymentMethod();
        if (paymentDetails) {
          handleSelectPaymentMethod(paymentDetails);
        }
      });

      setDropinInstance(instance);
    });
  }, [authorizationToken, dropinContainerRef.current]);

  useEffect(() => () => {
    if (dropinInstance) {
      const instance: Dropin = dropinInstance;
      instance.teardown();
    }
  }, [dropinInstance]);

  return (
    <>
      <div ref={dropinContainerRef} className={styles.dropin} />
      {paymentErrorMessage && (
        <div className={styles.payment_error}>
          <WarningIcon />
          <span>{paymentErrorMessage}</span>
        </div>
      )}
    </>
  );
}
